<template>
  <div class="empty-state d-flex flex-column px-6" data-locator="empty-state">
    <template v-if="isConversationPending">
      <v-skeleton-loader
        class="align-self-center mb-2 w-100 transparent-skeleton-loader"
        type="heading, text@4"
      />
    </template>
    <template v-else>
      <div
        class="font-weight-bold text--large text-purple-darken-1 text-center mb-6"
        data-locator="ai-empty-title"
      >
        {{ $t(`${TRANSLATION_KEY_PREFIX}.hereToHelpYou`) }}
      </div>
      <div class="d-flex flex-column row-gap-4">
        <AIAssistantQuestionTypePicker
          v-if="shouldShowQuestionTypePicker"
          @select="onSelectType"
        />

        <div v-else-if="isAskDoc" data-locator="ai-empty-ask-doc">
          <div class="text--small">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.askDoc.content`) }}
          </div>

          <ul class="px-6 text--small">
            <template v-for="(item, index) in $tm(`${TRANSLATION_KEY_PREFIX}.askDoc.list`)" :key="index">
              <li>{{ item }}</li>
            </template>
          </ul>
        </div>

        <div v-else data-locator="ai-empty-ask">
          <div>{{ $t(`${TRANSLATION_KEY_PREFIX}.ask.content`) }}</div>

          <ul class="px-6">
            <template v-for="(item, index) in $tm(`${TRANSLATION_KEY_PREFIX}.ask.list`)" :key="index">
              <li>{{ item }}</li>
            </template>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { AIAssistantActionTypes } from '@/store/modules/ai-assistant/action_types'
import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import { AIAssistantConversation, AIAssistantQuestionTypes, type AIAssistantQuestionType } from '@/types/ai-assistant'

import AIAssistantQuestionTypePicker from './AIAssistantQuestionTypePicker.vue'
import { toast } from '../utils/toast'

const { t } = useI18n()

const emit = defineEmits<{
  'update:showFooter': [value: boolean]
}>()

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPane'

const store = useStore()

const questionType = computed(() => store.state.aiAssistant.questionType as AIAssistantQuestionType | null)
const shouldShowQuestionTypePicker = computed(() => store.getters['aiAssistant/shouldShowQuestionTypePicker'])
const isAskDoc = computed(() => questionType.value === AIAssistantQuestionTypes.ASK_DOC)
const isConversationPending = computed(() => store.state.aiAssistant.postConversationPending)

watch(
  shouldShowQuestionTypePicker,
  (value) => emit('update:showFooter', !value),
  { immediate: true },
)

async function onSelectType(value: AIAssistantQuestionType) {
  store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_QUESTION_TYPE}`, value)
  if (value === AIAssistantQuestionTypes.ASK_DOC) {
    try {
      const conversation = await store.dispatch(
        `aiAssistant/${AIAssistantActionTypes.POST_CONVERSATION}`,
        value,
      ) as AIAssistantConversation
      store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_CONVERSATION_ACTIVE_ID}`, conversation.id)
      store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_QUESTION_TYPE}`, value)
    } catch (error) {
      console.error(error)
      toast.error(t(`${TRANSLATION_KEY_PREFIX}.askDoc.conversationCreationError`))
      store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_QUESTION_TYPE}`, null)
    }
  } else {
    // Later down the line, both of those will need to be set when posting a conversation,
    // but for now since the ask flow doesn't require a conversation,
    // we can set the question type right away to avoid an extra loading state
    store.commit(`aiAssistant/${AIAssistantMutationTypes.SET_QUESTION_TYPE}`, value)
  }
}
</script>

<style scoped lang="scss">
.empty-state {
  padding-top: 120px;
}

.transparent-skeleton-loader {
  background-color: transparent;
  :deep(.v-skeleton-loader__bone) {
    background-color: #CED6DE;
  }
}
</style>
